import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import BackToTop from "@/components/BackToTop";
import CanonicalLinkNormalizer from "@/components/CanonicalLinkNormalizer";
import ContactLinkTracking from "@/components/ContactLinkTracking";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";

// ─── Browser route chunks ────────────────────────────────────────────────────
// Indexable pages are synchronously imported by StaticAppRoutes.tsx, which is
// used only by the prerender build. Browser visitors load just the route they
// open, keeping the full page catalogue out of the homepage JavaScript payload.
const Home = lazy(() => import("./pages/Home"));
const SpecialtiesPage = lazy(() => import("./pages/Specialties"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AboutPage = lazy(() => import("./pages/About"));
const ContactPage = lazy(() => import("./pages/Contact"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const BookPage = lazy(() => import("./pages/Book"));
const BookConfirmed = lazy(() => import("./pages/BookConfirmed"));
const GrowthBrief = lazy(() => import("./pages/GrowthBrief"));
const PracticeVisibility = lazy(() => import("./pages/PracticeVisibility"));
const PracticeVisibilityScan = lazy(() => import("./pages/PracticeVisibilityScan"));
const PracticeVisibilityReport = lazy(() => import("./pages/PracticeVisibilityReport"));
const PrivacyPage = lazy(() => import("./pages/Privacy"));
const TermsPage = lazy(() => import("./pages/Terms"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const FreeReport = lazy(() => import("./pages/FreeReport"));
const FreeReportOffer = lazy(() => import("./pages/FreeReportOffer"));
const SeoAudit = lazy(() => import("./pages/SeoAudit"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const BlogPage = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const FractionalCMO = lazy(() => import("./pages/services/FractionalCMO"));
const SEOPage = lazy(() => import("./pages/services/SEO"));
const GoogleAdsPage = lazy(() => import("./pages/services/GoogleAds"));
const WebsiteDesignPage = lazy(() => import("./pages/services/WebsiteDesign"));
const AIAutomationPage = lazy(() => import("./pages/services/AIAutomation"));
const ContentMarketingPage = lazy(
  () => import("./pages/services/ContentMarketing")
);
const BrandDesignPage = lazy(() => import("./pages/services/BrandDesign"));
const AIVisibilityPage = lazy(() => import("./pages/services/AIVisibility"));
const SocialMediaPage = lazy(() => import("./pages/services/SocialMedia"));
const HealthcareSEOPage = lazy(() => import("./pages/services/HealthcareSEO"));
const GoogleAdsHealthcarePage = lazy(
  () => import("./pages/services/GoogleAdsHealthcare")
);
const ContentMarketingHealthcarePage = lazy(
  () => import("./pages/services/ContentMarketingHealthcare")
);
const WebsiteStrategyPage = lazy(
  () => import("./pages/services/WebsiteStrategy")
);
const EmailMarketingHealthcarePage = lazy(
  () => import("./pages/services/EmailMarketingHealthcare")
);
const SpeechPathologyPage = lazy(
  () => import("./pages/specialties/SpeechPathology")
);
const OccupationalTherapyPage = lazy(
  () => import("./pages/specialties/OccupationalTherapy")
);
const DentalPage = lazy(() => import("./pages/specialties/Dental"));
const GPMedicalPage = lazy(() => import("./pages/specialties/GPMedical"));
const PaediatricPage = lazy(() => import("./pages/specialties/Paediatric"));
const PrivatePracticePage = lazy(
  () => import("./pages/specialties/PrivatePractice")
);
const ExercisePhysiologyPage = lazy(
  () => import("./pages/specialties/ExercisePhysiology")
);
const MobileHealthcarePage = lazy(
  () => import("./pages/specialties/MobileHealthcare")
);
const WomensHealthPage = lazy(() => import("./pages/specialties/WomensHealth"));
const DieteticsPage = lazy(() => import("./pages/specialties/Dietetics"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const SolutionsIndex = lazy(() => import("./pages/SolutionsIndex"));
const OttoPractice = lazy(() => import("./pages/OttoPractice"));
const SeoSiloPage = lazy(() => import("./pages/SeoSilo"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));

// Gated admin, client and landing-page routes, plus optional widgets.

const AdminPage = lazy(() => import("./pages/Admin"));
const ClientOS = lazy(() => import("./pages/ClientOS"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const LpGoogleAds = lazy(() => import("./pages/LpGoogleAds"));
const LpGoogleAdsThankYou = lazy(() => import("./pages/LpGoogleAdsThankYou"));

const DeliveryCommitmentPopup = lazy(
  () => import("@/components/DeliveryCommitmentPopup")
);

// Minimal fallback shown while a lazy chunk loads (only used for admin/PPC pages now)
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-[#19D1C5] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export function AppRoutes() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* PPC landing pages — no nav/footer/chat */}
        <Route path="/lp/google-ads" component={LpGoogleAds} />
        <Route
          path="/lp/google-ads/thank-you"
          component={LpGoogleAdsThankYou}
        />

        <Route path="/" component={Home} />
        <Route path="/allied-health-marketing">
          {() => <SeoLandingPage path="/allied-health-marketing/" />}
        </Route>
        <Route path="/specialties" component={SpecialtiesPage} />
        <Route path="/about" component={AboutPage} />

        {/* Service routes — 301-style client redirects to /solutions/* */}
        <Route path="/services/fractional-cmo">
          {() => {
            window.location.replace("/solutions/fractional-cmo/");
            return null;
          }}
        </Route>
        <Route path="/services/seo">
          {() => {
            window.location.replace("/solutions/healthcare-seo/");
            return null;
          }}
        </Route>
        <Route path="/services/google-ads">
          {() => {
            window.location.replace("/solutions/google-ads/");
            return null;
          }}
        </Route>
        <Route path="/services/website-design">
          {() => {
            window.location.replace("/solutions/website-design/");
            return null;
          }}
        </Route>
        <Route path="/services/ai-automation">
          {() => {
            window.location.replace("/solutions/ai-automation/");
            return null;
          }}
        </Route>
        <Route path="/services/content-marketing">
          {() => {
            window.location.replace("/solutions/content-marketing/");
            return null;
          }}
        </Route>
        <Route path="/services/brand-design">
          {() => {
            window.location.replace("/solutions/brand-design/");
            return null;
          }}
        </Route>
        <Route path="/services/ai-visibility">
          {() => {
            window.location.replace("/solutions/ai-visibility/");
            return null;
          }}
        </Route>
        <Route path="/services/social-media">
          {() => {
            window.location.replace("/solutions/social-media/");
            return null;
          }}
        </Route>
        <Route path="/free-report">{() => <FreeReport />}</Route>
        <Route path="/free-ai-report-offer" component={FreeReportOffer} />
        <Route path="/seo-audit" component={SeoAudit} />
        <Route path="/thank-you" component={ThankYou} />

        {/* Specialty routes */}
        <Route path="/specialties/physiotherapy">
          {() => <SeoLandingPage path="/specialties/physiotherapy/" />}
        </Route>
        <Route
          path="/specialties/speech-pathology"
          component={SpeechPathologyPage}
        />
        <Route
          path="/specialties/occupational-therapy"
          component={OccupationalTherapyPage}
        />
        <Route path="/specialties/dental" component={DentalPage} />
        <Route path="/specialties/psychology">
          {() => <SeoLandingPage path="/specialties/psychology/" />}
        </Route>
        <Route path="/specialties/chiropractic">
          {() => <SeoLandingPage path="/specialties/chiropractic/" />}
        </Route>
        <Route path="/specialties/optometry">
          {() => <SeoLandingPage path="/specialties/optometry/" />}
        </Route>
        <Route path="/specialties/gp-medical" component={GPMedicalPage} />
        <Route path="/specialties/paediatric" component={PaediatricPage} />
        <Route
          path="/specialties/private-practice"
          component={PrivatePracticePage}
        />
        <Route
          path="/specialties/exercise-physiology"
          component={ExercisePhysiologyPage}
        />
        <Route
          path="/specialties/mobile-healthcare"
          component={MobileHealthcarePage}
        />
        <Route path="/specialties/ndis">
          {() => <SeoLandingPage path="/specialties/ndis/" />}
        </Route>
        <Route path="/specialties/womens-health" component={WomensHealthPage} />
        <Route path="/specialties/podiatry">
          {() => <SeoLandingPage path="/specialties/podiatry/" />}
        </Route>
        <Route path="/specialties/dietetics" component={DieteticsPage} />

        {/* New SEO-targeted service routes — redirect to /solutions/* */}
        <Route path="/services/healthcare-seo">
          {() => {
            window.location.replace("/solutions/healthcare-seo/");
            return null;
          }}
        </Route>
        <Route path="/services/google-ads-healthcare">
          {() => {
            window.location.replace("/solutions/google-ads/");
            return null;
          }}
        </Route>
        <Route path="/services/content-marketing-healthcare">
          {() => {
            window.location.replace("/solutions/content-marketing/");
            return null;
          }}
        </Route>
        <Route path="/services/website-strategy">
          {() => {
            window.location.replace("/solutions/website-design/");
            return null;
          }}
        </Route>
        <Route path="/services/email-marketing-healthcare">
          {() => {
            window.location.replace("/solutions/email-marketing/");
            return null;
          }}
        </Route>

        {/* Email Marketing — solutions route */}
        <Route
          path="/solutions/email-marketing"
          component={EmailMarketingHealthcarePage}
        />

        {/* Standalone pages */}
        <Route path="/contact" component={ContactPage} />
        <Route path="/faq" component={FAQPage} />
        <Route path="/growth-brief" component={GrowthBrief} />
        <Route path="/practice-visibility/scan/:publicId" component={PracticeVisibilityScan} />
        <Route path="/practice-visibility/r/:token" component={PracticeVisibilityReport} />
        <Route path="/practice-visibility/report/:token" component={PracticeVisibilityReport} />
        <Route path="/practice-visibility" component={PracticeVisibility} />
        <Route path="/book/confirmed" component={BookConfirmed} />
        <Route path="/book" component={BookPage} />
        <Route
          path="/admin/revenue"
          component={() => <AdminPage initialTool="revenue" />}
        />
        <Route
          path="/admin/case-studies"
          component={() => <AdminPage initialTool="case-studies" />}
        />
        <Route path="/admin" component={() => <AdminPage />} />
        <Route path="/client-os" component={ClientOS} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />

        {/* Backend-managed solutions catalog */}
        <Route path="/solutions" component={SolutionsIndex} />
        <Route path="/solutions/fractional-cmo" component={FractionalCMO} />
        <Route path="/solutions/ai-automation" component={OttoPractice} />
        <Route path="/solutions/google-ads/dentists">
          {() => <SeoLandingPage path="/solutions/google-ads/dentists/" />}
        </Route>
        <Route path="/solutions/website-design/ndis">
          {() => <SeoLandingPage path="/solutions/website-design/ndis/" />}
        </Route>
        <Route path="/solutions/:slug" component={SolutionPage} />

        {/* Profession parent pages precede the dynamic specialty-city route. */}
        <Route path="/seo/chiropractic">
          {() => <SeoLandingPage path="/seo/chiropractic/" />}
        </Route>
        <Route path="/seo/physiotherapy">
          {() => <SeoLandingPage path="/seo/physiotherapy/" />}
        </Route>
        <Route path="/seo/psychology">
          {() => <SeoLandingPage path="/seo/psychology/" />}
        </Route>
        <Route path="/seo/optometry">
          {() => <SeoLandingPage path="/seo/optometry/" />}
        </Route>
        <Route path="/seo/podiatry">
          {() => <SeoLandingPage path="/seo/podiatry/" />}
        </Route>

        {/* Blog routes */}
        <Route path="/seo/:slug" component={SeoSiloPage} />
        <Route path="/blog/:slug" component={BlogPostPage} />
        <Route path="/blog" component={BlogPage} />

        {/* Client Portal — public, token-based */}
        <Route path="/portal" component={ClientPortal} />

        {/* Email unsubscribe — public, token-based */}
        <Route path="/unsubscribe" component={Unsubscribe} />

        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const [location] = useLocation();
  const isLpPage = location.startsWith("/lp/");
  const isPracticeVisibilityFlow = location === "/practice-visibility" || location.startsWith("/practice-visibility/");
  const cleanPath = location.replace(/\/+$/, "") || "/";
  const suppressDeliveryPopup = new Set([
    "/allied-health-marketing",
    "/specialties/ndis",
    "/seo/chiropractic",
    "/seo/physiotherapy",
    "/seo/psychology",
    "/seo/optometry",
    "/seo/podiatry",
  ]).has(cleanPath);
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <Toaster />
            <ScrollToTop />
            <CanonicalLinkNormalizer />
            <ContactLinkTracking pathname={location} />
            <main>
              <AppRoutes />
            </main>
            {!isLpPage && !isPracticeVisibilityFlow && !suppressDeliveryPopup && (
              <Suspense fallback={null}>
                <DeliveryCommitmentPopup pathname={location} />
              </Suspense>
            )}
            {!isLpPage && <BackToTop />}
          </TooltipProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
